import { CommentSharp, Done, DoNotDisturb, InfoSharp } from "@mui/icons-material";
import { Badge, Box, Button, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
	formatDate,
	formatDateTime,
	formatHoursMinutes,
} from "@repo/legacy/components/helper/Index";
import { useAllTimeApprovalsDataSource } from "@repo/legacy/components/shared/datasources/AllTimeApprovalsDataSource";
import ConformationDialogWithContext from "@repo/legacy/components/shared/dialogs/ConformationWithContext";
import DialogShowDescription from "@repo/legacy/components/shared/dialogs/DialogShowDescription";
import DialogTimeApproval from "@repo/legacy/components/shared/dialogs/DialogTimeApproval";
import DialogTimeApprovalDetailsAccept from "@repo/legacy/components/shared/dialogs/DialogTimeApprovalDetailsAccept";
import DetailPanelContentTimeApprovals from "@repo/legacy/components/shared/lists/DetailPanelContentTimeApprovals";
import List from "@repo/legacy/components/shared/lists/List";
import Dropdown from "@repo/legacy/components/shared/sections/Dropdown";
import {
	ApprovalTimeEntryStatus,
	useDownloadTimeSheetLazyQuery,
	useGetAllProjectsNameLazyQuery,
	useGetAllFreelancersAndEmployeesLazyQuery,
} from "@repo/server-api";
import {
	BoundAsyncMultiSelect,
	DownloadButton,
	useAsyncSelectStaticListHook,
} from "@repo/shared/components";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useEffect, useMemo, useState } from "react";

import type { GridCellParams, GridColDef } from "@mui/x-data-grid";
import type { Contact, Project, ProjectTimeApproval } from "@repo/server-api";
import type { OptionType, ProjectTimeApprovalExtended } from "@repo/server-api/types";

export function ProjectManagerTimeSheets() {
	const { t, i18n } = useTranslation();

	const [openDialog, setOpenDialog] = useState<boolean>(false);
	const [openDialogDetails, setOpenDialogDetails] = useState<boolean>(false);
	const [openConformationDialog, setOpenConformationDialog] = useState<boolean>(false);
	const [openDialogComment, setOpenDialogComment] = useState<boolean>(false);
	const [timeApproval, setTimeApproval] = useState<ProjectTimeApprovalExtended | null>(null);

	// TODO Remove this after optimization
	const [timeApprovalComments, setTimeApprovalComments] =
		useState<ProjectTimeApprovalExtended | null>(null);
	const [timeApprovalDetails, setTimeApprovalDetails] =
		useState<ProjectTimeApprovalExtended | null>(null);
	const [timeApprovalDelete, setTimeApprovalDelete] = useState<ProjectTimeApproval | null>(null);

	const [message, setMessage] = useState<string>("");

	const allTimeApprovalsDataSource = useAllTimeApprovalsDataSource();

	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProject, setSelectedProject] = useState<OptionType | null>(null);

	const [employees, setEmployees] = useState<Contact[]>([]);
	const [selectedEmployee, setSelectedEmployee] = useState<OptionType | null>(null);

	const [year, setYear] = useState<dayjs.Dayjs>(dayjs());
	const [openYear, setOpenYear] = useState<boolean>(false);

	const [statusFields, setStatusFields] = useState<
		{ id: string; name: string; status: ApprovalTimeEntryStatus }[]
	>([
		{
			id: "PENDING",
			name: t("time-sheets.status.pending", { ns: "enum" }),
			status: ApprovalTimeEntryStatus.Pending,
		},
	]);

	const allStatusFields = useMemo(
		() => [
			{
				id: "APPROVED",
				name: t("time-sheets.status.approved", { ns: "enum" }),
				status: ApprovalTimeEntryStatus.Approved,
			},
			{
				id: "REJECTED",
				name: t("time-sheets.status.rejected", { ns: "enum" }),
				status: ApprovalTimeEntryStatus.Rejected,
			},
			{
				id: "PENDING",
				name: t("time-sheets.status.pending", { ns: "enum" }),
				status: ApprovalTimeEntryStatus.Pending,
			},
			{
				id: "DELETED",
				name: t("time-sheets.status.deleted", { ns: "enum" }),
				status: ApprovalTimeEntryStatus.Deleted,
			},
		],
		[t],
	);

	const searchStatusHook = useAsyncSelectStaticListHook<{
		id: string;
		name: string;
		status: ApprovalTimeEntryStatus;
	}>(allStatusFields);

	const [getAllProjects] = useGetAllProjectsNameLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			first: 100,
		},
		onCompleted(data) {
			if (data.allProjects?.nodes) {
				setProjects(data.allProjects.nodes as Project[]);
			}
		},
	});

	const [getEmployees] = useGetAllFreelancersAndEmployeesLazyQuery({
		fetchPolicy: "no-cache",
		variables: {
			first: 100,
		},
		onCompleted(data) {
			if (data.allContacts?.nodes) {
				setEmployees(data.allContacts.nodes as unknown as Contact[]);
			}
		},
	});

	const [downloadTimeSheet] = useDownloadTimeSheetLazyQuery({
		fetchPolicy: "no-cache",
	});

	const getContacts = (searchName: string) => {
		getEmployees({
			variables: {
				first: 100,
				name: searchName,
			},
		});
	};

	const getProjects = (searchName: string) => {
		getAllProjects({
			variables: {
				first: 100,
				name: searchName,
			},
		});
	};

	useEffect(() => {
		getAllProjects();
		getEmployees();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const columns: GridColDef<ProjectTimeApproval>[] = useMemo(
		() => [
			{
				field: "project.name",
				headerName: t("project-name"),
				width: 150,
				flex: 1,
				sortable: true,
				valueGetter: (value, row) => row.project?.name || "",
				renderCell: (params: GridCellParams) => {
					return params.row.project?.name ?? "";
				},
			},
			{
				field: "number",
				headerName: t("approval-number"),
				width: 180,
				sortable: true,
				valueGetter: (value, row) => row.number,
				renderCell: (params: GridCellParams) => {
					return params.row.number;
				},
			},
			{
				field: "totalHours",
				headerName: t("total-hours"),
				width: 150,
				sortable: true,
				valueGetter: (value, row) => row.totalMinutes || 0,
				renderCell: (params: GridCellParams) => {
					return formatHoursMinutes(params.row.totalMinutes || 0);
				},
			},
			{
				field: "fromTo",
				headerName: t("start-till-end"),
				width: 250,
				headerAlign: "center",
				align: "center",
				sortable: true,
				valueGetter: (value, row) => {
					if (!row.from || !row.to) return "";
					const from = formatDate(row.from, i18n.language);
					const to = formatDate(row.to, i18n.language);
					return `${from} → ${to}`;
				},
				renderCell: (params: GridCellParams) => {
					if (!params.row.from || !params.row.to) return "";
					const from = formatDate(params.row.from, i18n.language);
					const to = formatDate(params.row.to, i18n.language);
					return `${from} → ${to}`;
				},
			},
			{
				field: "status",
				headerName: t("status"),
				width: 150,
				align: "center",
				sortable: true,
				valueGetter: (value, row) => row.status,
				renderCell: (params: GridCellParams) => {
					let fontColor = "#000";
					let bgColor = "#f9a825";
					switch (params.row.status) {
						case "APPROVED":
							fontColor = "#fff";
							bgColor = "#4caf50";
							break;
						case "DELETED":
						case "REJECTED":
							fontColor = "#000";
							bgColor = "#f44336";
							break;
						case "PENDING":
							fontColor = "#000";
							bgColor = "#f9a825";
							break;
						case "CANCELED":
							fontColor = "#000";
							bgColor = "#9e9e9e";
							break;
						case "OPEN":
							fontColor = "#000";
							bgColor = "lightblue";
							break;

						default:
							fontColor = "#000";
							bgColor = "#f9a825";
							break;
					}
					return (
						<div
							style={{
								justifyContent: "center",
								alignItems: "center",
								display: "flex",
								height: "100%",
							}}>
							<Badge
								style={{
									justifyContent: "center",
									alignItems: "center",
									backgroundColor: bgColor,
									color: fontColor,
									borderRadius: "10px",
									padding: "5px 10px",
									fontSize: "12px",
									fontWeight: "bold",
									width: "50px",
									height: "15px",
								}}>
								{t(`time-sheets.status.${params.row.status.toLowerCase()}`, {
									ns: "enum",
								})}
							</Badge>
						</div>
					);
				},
			},
			{
				field: "createdBy",
				headerName: t("created-by"),
				width: 150,
				sortable: true,
				valueGetter: (value, row) => row.createdBy.displayName,
				renderCell: (params: GridCellParams) => {
					return params.row.createdBy.displayName;
				},
			},
			{
				field: "timestamp",
				headerName: t("created-at"),
				width: 150,
				sortable: true,
				valueGetter: (value, row) => formatDateTime(row.timestamp, i18n.language),
				renderCell: (params: GridCellParams) => {
					return formatDateTime(params.row.timestamp, i18n.language);
				},
			},
		],
		[t, i18n.language],
	);

	const mainColumns = useMemo(
		() => [
			...columns,
			{
				field: "actions",
				headerName: t("actions"),
				sortable: false,
				minWidth: 350,
				renderCell: (params: GridCellParams) => {
					const openComment = (e: ProjectTimeApproval | null) => {
						setTimeApprovalComments(e);
						setOpenDialogComment(true);
					};

					const openDetails = (e: ProjectTimeApproval | null) => {
						setTimeApprovalDetails(e);
						setOpenDialogDetails(true);
					};

					const openReject = (e: ProjectTimeApproval | null) => {
						setTimeApprovalDelete(e);
						setOpenConformationDialog(true);
					};
					const startDownload = async (approvalId: string) => {
						const result = await downloadTimeSheet({
							variables: {
								projectTimeApprovalId: approvalId,
							},
						});

						if (!result || !result.data) {
							throw new Error("Download failed");
						}

						const url = result.data?.downloadTimeSheet;
						if (url) {
							window.open(url, "_blank");
						}
					};

					return (
						<Box sx={{ display: "flex", flexWrap: "nowrap" }}>
							<DownloadButton onDownload={() => startDownload(params.row.id)} />
							<Button
								disabled={!params.row.description}
								data-testid="edit-button"
								onClick={() => {
									openComment(params.row);
								}}>
								<CommentSharp />
							</Button>
							<Button
								data-testid="edit-button"
								onClick={() => {
									openDetails(params.row);
								}}>
								<InfoSharp />
							</Button>
							<Button
								disabled={
									params.row.status !== ApprovalTimeEntryStatus.Pending
										? params.row.status !== ApprovalTimeEntryStatus.Rejected
										: undefined
								}
								data-testid="edit-button"
								onClick={() => {
									if (allTimeApprovalsDataSource.approveTimeApproval) {
										allTimeApprovalsDataSource.approveTimeApproval(params.row.id);
									}
								}}>
								<Done />
							</Button>
							<Button
								disabled={
									params.row.status !== ApprovalTimeEntryStatus.Pending
										? params.row.status !== ApprovalTimeEntryStatus.Approved
										: undefined
								}
								data-testid="edit-button"
								onClick={() => {
									openReject(params.row);
								}}>
								<DoNotDisturb />
							</Button>
						</Box>
					);
				},
			},
		],
		[t, columns, allTimeApprovalsDataSource],
	);

	const detailsColumns = [
		{
			field: "space",
			headerName: "",
			width: 50,
		},
		...columns,
		{
			field: "actions",
			width: 150,
		},
	];

	const refreshData = () => {
		allTimeApprovalsDataSource.refresh?.();
	};

	const [search, setSearch] = useState<string>("");

	// Memoize date strings to prevent infinite re-renders
	const startYearDate = useMemo(() => dayjs(year).startOf("year").format("YYYY-MM-DD"), [year]);

	const endYearDate = useMemo(() => dayjs(year).endOf("year").format("YYYY-MM-DD"), [year]);

	// Memoize status array to prevent infinite re-renders
	const filterStatuses = useMemo(() => statusFields.map((s) => s.status), [statusFields]);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<h3 className="purpleHeader">{t("time-sheets")}</h3>
			<DialogShowDescription
				open={openDialogComment}
				setOpen={setOpenDialogComment}
				item={timeApprovalComments}
			/>
			<DialogTimeApproval
				open={openDialog}
				setOpen={setOpenDialog}
				refreshData={refreshData}
				timeApproval={timeApproval}
				setTimeApproval={setTimeApproval}
			/>
			<DialogTimeApprovalDetailsAccept
				open={openDialogDetails}
				setOpen={setOpenDialogDetails}
				refreshData={refreshData}
				timeApproval={timeApprovalDetails}
				setTimeApproval={setTimeApprovalDetails}
				allTimeApprovalsDataSource={allTimeApprovalsDataSource}
			/>
			<ConformationDialogWithContext
				openPopup={openConformationDialog}
				setOpenDeleteConformation={setOpenConformationDialog}
				text={t("projects.timeSheets.rejectConfirmation")}
				message={message}
				setMessage={setMessage}
				handleYes={() => {
					if (allTimeApprovalsDataSource.rejectDataWithReason && timeApprovalDelete?.id)
						allTimeApprovalsDataSource.rejectDataWithReason(timeApprovalDelete.id, message);
					setOpenConformationDialog(false);
					setMessage("");
				}}
				handleClose={() => {
					setMessage("");
				}}
			/>
			<List
				showOnlyChildren
				dataSource={allTimeApprovalsDataSource}
				columns={mainColumns as GridCellParams<ProjectTimeApproval>[]}
				hasDetailPanelContent
				detailPanelContent={(row?: ProjectTimeApproval) =>
					row?.history.length ? (
						<DetailPanelContentTimeApprovals
							history={row?.history}
							columns={detailsColumns as GridCellParams[]}
						/>
					) : null
				}
				startYearDate={startYearDate}
				endYearDate={endYearDate}
				searchBy={search}
				filterByProjectId={selectedProject?.item?.id || null}
				filterByEmployeeId={selectedEmployee?.item?.id || null}
				filterByStatuses={filterStatuses}>
				<Box className="left" style={{ marginTop: 10 }} sx={{ minWidth: 120 }}>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language || "de"}>
						<DatePicker
							open={openYear}
							onClose={() => {
								setOpenYear(false);
							}}
							onOpen={() => {
								setOpenYear(true);
							}}
							label={t("year")}
							views={["year"]}
							value={year}
							onChange={(newValue) => {
								setYear(newValue as dayjs.Dayjs);
							}}
							slotProps={{
								textField: {
									onClick: () => {
										setOpenYear(!openYear);
									},
								},
							}}
						/>
					</LocalizationProvider>
				</Box>
				<Box className="left" style={{ marginLeft: 20, marginTop: 10 }} sx={{ minWidth: 200 }}>
					<TextField
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
						}}
						variant="outlined"
						label={t("search")}
						placeholder={t("placeholder.search")}
						fullWidth
					/>
				</Box>
				<Box className="left w-20" style={{ marginLeft: 20, marginTop: 10 }} sx={{ minWidth: 200 }}>
					<Dropdown
						dropdownType="project"
						doNotShowMenu
						elements={projects}
						element={{
							title: selectedProject?.title || "",
							inputValue: selectedProject?.inputValue || "",
							item: selectedProject?.item || null,
						}}
						setElement={(p) => {
							setSelectedProject(p);
						}}
						searchLabel={t("project")}
						searchPlaceholder={t("search-for-project")}
						getProjects={getProjects}
					/>
				</Box>
				<Box className="left w-20" style={{ marginLeft: 20, marginTop: 10 }} sx={{ minWidth: 200 }}>
					<Dropdown
						dropdownType="contact"
						doNotShowMenu
						elements={employees}
						element={{
							title: selectedEmployee?.title ?? "",
							inputValue: selectedEmployee?.inputValue ?? "",
							item: selectedEmployee?.item ?? null,
						}}
						setElement={(e) => {
							setSelectedEmployee(e);
						}}
						getContacts={getContacts}
						searchLabel={t("employee")}
						searchPlaceholder={t("search-for-employee")}
					/>
				</Box>
				<Box className="left w-20" style={{ marginLeft: 20, marginTop: 10 }} sx={{ minWidth: 200 }}>
					<BoundAsyncMultiSelect
						label={t("status")}
						boundedValue={{
							value: statusFields,
							onChangeValue: (v: { id: string; name: string; status: ApprovalTimeEntryStatus }[]) =>
								setStatusFields(v),
							errorKey: "statusFields",
						}}
						searchHook={searchStatusHook}
					/>
				</Box>
				<div className="clear" />
			</List>
		</Box>
	);
}
